/**
 * Sherwin-Williams Top 50 Most Popular Colors
 * Data includes official SW codes, names, hex values, color families, and LRV values
 */

import type { SherwinWilliamsColor } from '@/types/firestore';

export const SHERWIN_WILLIAMS_COLORS: SherwinWilliamsColor[] = [
  // Whites & Off-Whites
  { code: 'SW-7006', name: 'Extra White', hex: '#F1F0E7', family: 'White', lrv: 86 },
  { code: 'SW-7008', name: 'Alabaster', hex: '#F3EFE0', family: 'White', lrv: 82 },
  { code: 'SW-7012', name: 'Creamy', hex: '#F5ECD5', family: 'White', lrv: 81 },
  { code: 'SW-7014', name: 'Eider White', hex: '#E7E4DA', family: 'White', lrv: 73 },
  { code: 'SW-7015', name: 'Repose Gray', hex: '#C2BFB8', family: 'Gray', lrv: 58 },
  { code: 'SW-7016', name: 'Mindful Gray', hex: '#B4B0A7', family: 'Gray', lrv: 48 },
  { code: 'SW-7029', name: 'Agreeable Gray', hex: '#D1CBC0', family: 'Gray', lrv: 60 },
  { code: 'SW-7036', name: 'Accessible Beige', hex: '#D2C6B4', family: 'Neutral', lrv: 58 },
  { code: 'SW-7043', name: 'Worldly Gray', hex: '#B8B4A7', family: 'Gray', lrv: 47 },
  { code: 'SW-7044', name: 'Amazing Gray', hex: '#A9A79B', family: 'Gray', lrv: 42 },

  // Popular Grays
  { code: 'SW-7064', name: 'Passive', hex: '#CDC9C0', family: 'Gray', lrv: 60 },
  { code: 'SW-7069', name: 'Iron Ore', hex: '#434341', family: 'Gray', lrv: 6 },
  { code: 'SW-7072', name: 'Online', hex: '#5E5E5C', family: 'Gray', lrv: 13 },
  { code: 'SW-7073', name: 'Network Gray', hex: '#7D7D7B', family: 'Gray', lrv: 23 },
  { code: 'SW-7074', name: 'Software', hex: '#9B9B97', family: 'Gray', lrv: 35 },
  { code: 'SW-7641', name: 'Colonnade Gray', hex: '#BBB6A8', family: 'Gray', lrv: 48 },
  { code: 'SW-7642', name: 'Pavestone', hex: '#9F9A8C', family: 'Gray', lrv: 35 },
  { code: 'SW-7643', name: 'Pussywillow', hex: '#9E9585', family: 'Gray', lrv: 33 },
  { code: 'SW-9165', name: 'Gossamer Veil', hex: '#D9D4C7', family: 'Gray', lrv: 67 },
  { code: 'SW-9166', name: 'Drift of Mist', hex: '#E0DCCF', family: 'Gray', lrv: 71 },

  // Greiges (Gray-Beige)
  { code: 'SW-7023', name: 'Requisite Gray', hex: '#B2AFA5', family: 'Greige', lrv: 45 },
  { code: 'SW-7037', name: 'Balanced Beige', hex: '#C4B8A3', family: 'Greige', lrv: 49 },
  { code: 'SW-7046', name: 'Anonymous', hex: '#A9A597', family: 'Greige', lrv: 40 },
  { code: 'SW-7632', name: 'Modern Gray', hex: '#B8B3A1', family: 'Greige', lrv: 47 },
  { code: 'SW-7638', name: 'Jogging Path', hex: '#C8C1B1', family: 'Greige', lrv: 54 },

  // Blues
  { code: 'SW-6217', name: 'Topsail', hex: '#D1DFE2', family: 'Blue', lrv: 75 },
  { code: 'SW-6218', name: 'Tradewind', hex: '#ADC5C8', family: 'Blue', lrv: 61 },
  { code: 'SW-6219', name: 'Rain', hex: '#94B2B7', family: 'Blue', lrv: 47 },
  { code: 'SW-6226', name: 'Languid Blue', hex: '#D3DFE2', family: 'Blue', lrv: 75 },
  { code: 'SW-6243', name: 'Distance', hex: '#C5D5DA', family: 'Blue', lrv: 67 },
  { code: 'SW-6244', name: 'Naval', hex: '#2E3441', family: 'Blue', lrv: 4 },
  { code: 'SW-6252', name: 'Ice Cube', hex: '#D4E3E5', family: 'Blue', lrv: 76 },
  { code: 'SW-9140', name: 'Breezy', hex: '#D8E4E5', family: 'Blue', lrv: 77 },

  // Greens
  { code: 'SW-6119', name: 'Antique White', hex: '#F2E6D5', family: 'Neutral', lrv: 79 },
  { code: 'SW-6176', name: 'Liveable Green', hex: '#C5CEBD', family: 'Green', lrv: 61 },
  { code: 'SW-6177', name: 'Softened Green', hex: '#AAB9A5', family: 'Green', lrv: 48 },
  { code: 'SW-6178', name: 'Clary Sage', hex: '#9BAA91', family: 'Green', lrv: 41 },
  { code: 'SW-6179', name: 'Artichoke', hex: '#8A9B7C', family: 'Green', lrv: 33 },
  { code: 'SW-6183', name: 'Conservative Gray', hex: '#B1B9AA', family: 'Green', lrv: 49 },
  { code: 'SW-6199', name: 'Rare Gray', hex: '#C4D0BE', family: 'Green', lrv: 61 },
  { code: 'SW-9130', name: 'Evergreen Fog', hex: '#9DB0A2', family: 'Green', lrv: 45 },

  // Warm Neutrals & Beiges
  { code: 'SW-6106', name: 'Kilim Beige', hex: '#C4B097', family: 'Neutral', lrv: 46 },
  { code: 'SW-6107', name: 'Nomadic Desert', hex: '#B8A383', family: 'Neutral', lrv: 39 },
  { code: 'SW-6108', name: 'Latte', hex: '#A69070', family: 'Neutral', lrv: 30 },
  { code: 'SW-6121', name: 'Whole Wheat', hex: '#CAB897', family: 'Neutral', lrv: 49 },
  { code: 'SW-7036', name: 'Accessible Beige', hex: '#D2C6B4', family: 'Neutral', lrv: 58 },

  // Bold Accent Colors
  { code: 'SW-6258', name: 'Tricorn Black', hex: '#2F2E2D', family: 'Black', lrv: 3 },
  { code: 'SW-7005', name: 'Pure White', hex: '#EFF0E8', family: 'White', lrv: 84 },
  { code: 'SW-6124', name: 'Cardboard', hex: '#C19B6A', family: 'Warm', lrv: 35 },
  { code: 'SW-0077', name: 'Classic French Gray', hex: '#9AA4A5', family: 'Blue', lrv: 39 },
];

/**
 * Color families for grouping and filtering
 */
export const COLOR_FAMILIES = [
  'White',
  'Gray',
  'Greige',
  'Blue',
  'Green',
  'Neutral',
  'Warm',
  'Black',
] as const;

export type ColorFamily = typeof COLOR_FAMILIES[number];

/**
 * Get colors by family
 */
export function getColorsByFamily(family: ColorFamily): SherwinWilliamsColor[] {
  return SHERWIN_WILLIAMS_COLORS.filter((c) => c.family === family);
}

/**
 * Search colors by name or code
 */
export function searchColors(query: string): SherwinWilliamsColor[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];

  return SHERWIN_WILLIAMS_COLORS.filter(
    (c) =>
      c.name.toLowerCase().includes(lowerQuery) ||
      c.code.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get a color by its SW code
 */
export function getColorByCode(code: string): SherwinWilliamsColor | undefined {
  return SHERWIN_WILLIAMS_COLORS.find((c) => c.code === code);
}

/**
 * Default featured colors for new tenants
 * Popular, versatile colors that work in most renovation contexts
 */
export const DEFAULT_FEATURED_COLORS: string[] = [
  'SW-7029', // Agreeable Gray
  'SW-7008', // Alabaster
  'SW-7015', // Repose Gray
  'SW-6217', // Topsail
  'SW-6176', // Liveable Green
  'SW-7036', // Accessible Beige
  'SW-6244', // Naval
  'SW-7069', // Iron Ore
];
